// Typed API client for My Trips (Phase 1). Reuses the shared authApi axios
// instance (auto-attaches the JWT and handles refresh). Mirrors the backend
// routes in backend/routes/trips.js and the assign route in journeys.js.

import { authApi } from '../auth/api';
import {
  TripsListResponse,
  OrphanSummary,
  TripListItem,
  TripDetail,
  TripParams,
  TripStage,
  AssignPayload,
} from './types';
import { Strategy, AlternativePlan } from './strategies';

export async function fetchTrips(): Promise<TripsListResponse> {
  const res = await authApi.get('/trips');
  return res.data;
}

export async function fetchTripDetail(id: string): Promise<TripDetail> {
  const res = await authApi.get(`/trips/${id}`);
  return res.data;
}

export async function fetchOrphans(): Promise<OrphanSummary[]> {
  const res = await authApi.get('/trips/orphans');
  return res.data.orphans;
}

export interface CreateTripInput {
  title: string;
  stage?: TripStage;
  dateStart?: string | null;
  dateEnd?: string | null;
  params?: TripParams;
  coverTemplate?: string;
  coverPalette?: string;
}

export async function createTrip(input: CreateTripInput): Promise<TripListItem> {
  const res = await authApi.post('/trips', input);
  return res.data;
}

export async function updateTrip(id: string, patch: Partial<CreateTripInput>): Promise<TripListItem> {
  const res = await authApi.patch(`/trips/${id}`, patch);
  return res.data;
}

/**
 * Deep copy: the trip AND every journey alternative, deterministic row copies
 * (Paulo 2026-09-14 — the LLM regenerating from the brief never reproduces
 * the same trip, so "copy" must not go through generation).
 */
export async function copyTrip(id: string, title: string): Promise<{ tripId: string; mainJourneyId: string | null }> {
  const res = await authApi.post(`/trips/${id}/copy`, { title });
  return res.data;
}

export type DeleteDisposition = 'delete' | 'orphan' | 'move';

export async function deleteTrip(
  id: string,
  disposition: DeleteDisposition,
  targetTripId?: string,
): Promise<{ id: string; disposition: DeleteDisposition }> {
  const res = await authApi.delete(`/trips/${id}`, { data: { disposition, targetTripId } });
  return res.data;
}

/**
 * Bulk-delete a trip's alternatives. Without `keepJourneyId` the trip is left
 * empty ("Delete all alternatives"); with it, that one survives and the rest go
 * ("Delete other alternatives"). One transaction server-side, which also
 * re-resolves the trip's main journey.
 */
export async function deleteAlternatives(tripId: string, keepJourneyId?: string): Promise<{ deletedCount: number }> {
  const res = await authApi.delete(`/trips/${tripId}/alternatives`, {
    data: keepJourneyId ? { keepJourneyId } : {},
  });
  return res.data;
}

export async function setMainJourney(tripId: string, journeyId: string): Promise<TripListItem> {
  const res = await authApi.post(`/trips/${tripId}/main`, { journeyId });
  return res.data;
}

/** Move a journey to a trip, a new trip (newTripTitle), or orphan (tripId: null). */
export async function assignJourney(journeyId: string, payload: AssignPayload): Promise<{ tripId: string | null }> {
  const res = await authApi.patch(`/journeys/${journeyId}/assign`, payload);
  return res.data;
}

/**
 * Generate one alternative for a trip from its brief + a strategy. The journey
 * is created, attached to the trip, and (if it's the first) set as main, all
 * server-side. Returns the created journey (at least its id).
 */
export async function generateAlternative(tripId: string, strategy: Strategy, signal?: AbortSignal): Promise<{ id: string }> {
  const res = await authApi.post(`/trips/${tripId}/alternatives`, { strategy }, { signal });
  return res.data.journey;
}

/**
 * Generate the initial alternative SET for a freshly created trip: the "Jody
 * Smart Mix" (main) plus deterministic single-mode variants derived from it.
 * One AI itinerary call server-side; prices fill in async. Returns the created
 * journeys (Smart Mix first).
 */
export async function generateInitialAlternatives(tripId: string, signal?: AbortSignal): Promise<{ id: string }[]> {
  const res = await authApi.post(`/trips/${tripId}/initial-alternatives`, {}, { signal });
  return res.data.journeys;
}

/**
 * The trip's alternative PLAN — which alternatives to generate/offer and why
 * (decide-only; no generation happens). Server-side it is computed once per
 * brief version and persisted on the trip, so calling this is cheap when the
 * plan is fresh. Null when the trip's brief can't be planned yet.
 */
export async function planAlternatives(tripId: string): Promise<AlternativePlan | null> {
  const res = await authApi.post(`/trips/${tripId}/alternative-plan`, {});
  return res.data.plan ?? null;
}
